import {
  widgetCapabilities,
  type DashboardWidget,
  type DashboardWidgetWire,
  type WidgetDateRange,
  type WidgetKind,
} from '../models/widget.types'

import type { ChartFieldRoleSelection, ChartType, ReportFilter } from '@/features/reports'

export interface WidgetBuilderForm {
  id: string
  name: string
  kind: WidgetKind
  reportId: number | null
  partition: string | null
  level: string | null
  accountWorkspaces: (number | string)[]
  dimensions: string[]
  metrics: string[]
  chartType: ChartType | null
  fieldValues: ChartFieldRoleSelection
  filters: ReportFilter[]
  sortField: string | null
  breakdown: string | null
  rowCount: number | null
  matrixDimension: string | null
  previewMetric: string | null
  selectedKpi: number | null
  cardButton: boolean
  expandedColumns: string[]
  isAdLevel: boolean
  selectedDate: string | null
  compareType: string | null
  compareEnabled: boolean | null
  customDate: WidgetDateRange | null
  customCompareDate: WidgetDateRange | null
}

export const BUILDER_SUPPORTED_WIDGET_KINDS = [
  'table',
  'matrix',
  'chart',
  'double',
  'card',
  'list_table',
  'dashboard_chart',
  'score',
  'kpi',
] as const satisfies readonly WidgetKind[]

const SUPPORTED_KIND_SET: ReadonlySet<WidgetKind> = new Set(BUILDER_SUPPORTED_WIDGET_KINDS)

export function isWidgetBuilderEditable(kind: WidgetKind | null): kind is WidgetKind {
  return kind !== null && SUPPORTED_KIND_SET.has(kind)
}

export const BUILDER_OWNED_WIDGET_KEYS = [
  'visibleType',
  'reportId',
  'partition',
  'selectedLevel',
  'partition_only',
  'accountWorkspaces',
  'isAdLevel',
  'setDimensions',
  'setMetrics',
  'defaultBreakdown',
  'sortField',
  'rowCount',
  'matrixDimension',
  'previewMetric',
  'selectedTable',
  'chartDimensions',
  'chartMetrics',
  'chartSortMetric',
  'defaultChart',
  'fieldValues',
  'defaultFilters',
  'selectedDate',
  'compareType',
  'compareEnabled',
  'customDate',
  'customCompareDate',
  'selectedKpi',
  'cardButton',
  'expandedColumns',
] as const

const LEVEL_KEYS = [
  'reportId',
  'partition',
  'selectedLevel',
  'partition_only',
  'setDimensions',
  'setMetrics',
  'defaultBreakdown',
  'sortField',
  'rowCount',
  'matrixDimension',
  'previewMetric',
  'selectedTable',
  'chartDimensions',
  'chartMetrics',
  'chartSortMetric',
  'defaultChart',
  'fieldValues',
  'defaultFilters',
  'isAdLevel',
] as const

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function numberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : []
}

function filters(value: unknown): ReportFilter[] {
  return Array.isArray(value)
    ? value
        .filter((entry): entry is ReportFilter => record(entry) !== null)
        .map((entry) => ({ ...entry }))
    : []
}

function primaryDateRange(value: unknown): WidgetDateRange | null {
  const source = record(value)
  const start = text(source?.date_start)
  const end = text(source?.date_end)
  return start || end
    ? { ...(start ? { date_start: start } : {}), ...(end ? { date_end: end } : {}) }
    : null
}

function compareDateRange(primaryValue: unknown, compareValue: unknown): WidgetDateRange | null {
  const primary = record(primaryValue)
  const compare = record(compareValue)
  const start = text(compare?.compare_date_start) ?? text(primary?.compare_date_start)
  const end = text(compare?.compare_date_end) ?? text(primary?.compare_date_end)
  return start || end
    ? {
        ...(start ? { compare_date_start: start } : {}),
        ...(end ? { compare_date_end: end } : {}),
      }
    : null
}

export function widgetToBuilderForm(widget?: DashboardWidget | null): WidgetBuilderForm {
  const wire = widget?.wire ?? {}
  const selectedLevel = record(wire.selectedLevel)
  return {
    id: text(wire.id) ?? `widget-${Date.now()}`,
    name: text(wire.name) ?? '',
    kind: widget?.kind ?? 'table',
    reportId: numberValue(wire.reportId) ?? numberValue(selectedLevel?.report_id),
    partition: text(wire.partition) ?? text(selectedLevel?.partition_name),
    level: text(selectedLevel?.level),
    accountWorkspaces: Array.isArray(wire.accountWorkspaces)
      ? wire.accountWorkspaces.filter(
          (value): value is number | string =>
            typeof value === 'number' || typeof value === 'string',
        )
      : [],
    dimensions: stringList(wire.setDimensions),
    metrics: stringList(wire.setMetrics),
    chartType: text(wire.defaultChart) as ChartType | null,
    fieldValues: record(wire.fieldValues)
      ? Object.fromEntries(
          Object.entries(wire.fieldValues ?? {}).map(([key, value]) => [key, stringList(value)]),
        )
      : {},
    filters: filters(wire.defaultFilters),
    sortField: text(wire.sortField),
    breakdown: text(wire.defaultBreakdown),
    rowCount: numberValue(wire.rowCount),
    matrixDimension: text(wire.matrixDimension),
    previewMetric: text(wire.previewMetric),
    selectedKpi: numberValue(wire.selectedKpi),
    cardButton: wire.cardButton === true,
    expandedColumns: stringList(wire.expandedColumns),
    isAdLevel: wire.isAdLevel === true,
    selectedDate: text(wire.selectedDate),
    compareType: text(wire.compareType),
    compareEnabled: typeof wire.compareEnabled === 'boolean' ? wire.compareEnabled : null,
    customDate: primaryDateRange(wire.customDate),
    customCompareDate: compareDateRange(wire.customDate, wire.customCompareDate),
  }
}

function baseWire(form: WidgetBuilderForm, existing?: DashboardWidget | null): DashboardWidgetWire {
  const sameKind = existing?.kind === form.kind
  const base: DashboardWidgetWire = { ...(existing?.wire ?? {}) }
  if (!sameKind) for (const key of BUILDER_OWNED_WIDGET_KEYS) delete base[key]
  const visibleType =
    sameKind && typeof existing?.wire.visibleType === 'string'
      ? existing.wire.visibleType
      : (widgetCapabilities(form.kind)?.subType ?? form.kind)
  return { ...base, id: form.id, name: form.name.trim(), visibleType }
}

function applyLevelFields(wire: DashboardWidgetWire, form: WidgetBuilderForm): void {
  wire.accountWorkspaces = [...form.accountWorkspaces]
  wire.setDimensions = [...form.dimensions]
  wire.setMetrics = [...form.metrics]
  wire.defaultFilters = form.filters.map((filter) => ({ ...filter }))
  wire.sortField = form.sortField
  wire.defaultBreakdown = form.breakdown
  wire.rowCount = form.rowCount
  wire.isAdLevel = form.level?.toLowerCase() === 'ad' ? form.isAdLevel : false
  wire.partition_only = form.level === 'creative_image' || form.level === 'creative_video'
  if (form.reportId === null) delete wire.reportId
  else wire.reportId = form.reportId
  if (form.partition === null) delete wire.partition
  else wire.partition = form.partition
  if (form.reportId !== null && form.partition !== null) {
    wire.selectedLevel = {
      ...(record(wire.selectedLevel) ?? {}),
      report_id: form.reportId,
      partition_name: form.partition,
      ...(form.level ? { level: form.level } : {}),
    }
  } else delete wire.selectedLevel
}

/** What the chart's slots asked for, split from the table's own `set*` fields (v3 `emitComplete`). */
export interface ChartFieldSplit {
  dimensions: readonly string[]
  metrics: readonly string[]
}

function applyChartAndMatrixFields(
  wire: DashboardWidgetWire,
  form: WidgetBuilderForm,
  chartFields: ChartFieldSplit | null,
): void {
  if (['chart', 'double', 'dashboard_chart'].includes(form.kind)) {
    wire.chartDimensions = [...(chartFields?.dimensions ?? form.dimensions)]
    wire.chartMetrics = [...(chartFields?.metrics ?? form.metrics)]
    wire.defaultChart = form.chartType
    wire.fieldValues = Object.fromEntries(
      Object.entries(form.fieldValues).map(([key, value]) => [key, [...value]]),
    )
  } else {
    for (const key of [
      'defaultChart',
      'fieldValues',
      'chartDimensions',
      'chartMetrics',
      'chartSortMetric',
    ] as const)
      delete wire[key]
  }
  if (form.kind === 'matrix') {
    // v3 `emitComplete:966-970`: a matrix pivots its first row dimension against `matrixDimension`
    // and previews its first metric; both are derived here, never chosen separately.
    wire.matrixDimension = form.matrixDimension
    wire.defaultBreakdown = form.dimensions[0] ?? null
    wire.previewMetric = form.metrics[0] ?? form.previewMetric
  } else {
    delete wire.matrixDimension
    delete wire.previewMetric
  }
  wire.selectedTable = form.kind === 'matrix' ? 'Matrix' : form.kind === 'table' ? 'Table' : null
}

function applyKindSpecificFields(
  wire: DashboardWidgetWire,
  form: WidgetBuilderForm,
  existing?: DashboardWidget | null,
): void {
  if (form.kind === 'kpi' && form.selectedKpi !== null) wire.selectedKpi = form.selectedKpi
  else delete wire.selectedKpi
  if (form.kind === 'card') wire.cardButton = form.cardButton
  else delete wire.cardButton
  if (existing?.kind === form.kind && form.expandedColumns.length > 0) {
    wire.expandedColumns = [...form.expandedColumns]
  } else delete wire.expandedColumns
}

export function widgetFromForm(
  form: WidgetBuilderForm,
  existing?: DashboardWidget | null,
  chartFields: ChartFieldSplit | null = null,
): DashboardWidget {
  const wire = baseWire(form, existing)
  applyLevelFields(wire, form)
  applyChartAndMatrixFields(wire, form, chartFields)
  applyKindSpecificFields(wire, form, existing)
  wire.selectedDate = form.selectedDate
  wire.compareType = form.compareType
  wire.compareEnabled = form.compareEnabled
  wire.isCustomDashboard = existing?.wire.isCustomDashboard ?? true
  if (form.selectedDate === 'Custom' && form.customDate) {
    wire.customDate = { ...form.customDate }
  } else delete wire.customDate
  if (form.compareEnabled === true && form.compareType === 'Custom' && form.customCompareDate) {
    wire.customCompareDate = { ...form.customCompareDate }
  } else delete wire.customCompareDate
  if (!widgetCapabilities(form.kind)?.needsLevel) for (const key of LEVEL_KEYS) delete wire[key]
  return { kind: form.kind, wire }
}
